import React from 'react';
import { X } from 'lucide-react';

/**
 * Cabecera reutilizable para `DialogShell` (prop `header`). Encapsula la estructura
 * (ícono + título + subtítulo + botón de cierre) que de otro modo se duplica entre modales.
 * El botón X invoca `onClose`: cablearlo al cierre protegido del modal (`requestClose`/`closeRef`)
 * para que la confirmación de cambios sin guardar también salte desde la X.
 */
type ModalHeaderVariant = 'emerald' | 'gradient-blue' | 'gradient-red';

interface VariantStyles {
  readonly bar: string;
  readonly iconWrap: string;
  readonly title: string;
  readonly subtitle: string;
  readonly close: string;
}

const VARIANTS: Record<ModalHeaderVariant, VariantStyles> = {
  emerald: {
    bar: 'bg-white border-b border-slate-200 p-6',
    iconWrap: 'bg-emerald-100 p-2 rounded-lg',
    title: 'text-2xl font-bold text-slate-800',
    subtitle: 'text-sm text-slate-500',
    close: 'p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600'
  },
  'gradient-blue': {
    bar: 'bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6',
    iconWrap: 'bg-white/20 p-2 rounded-lg',
    title: 'text-2xl font-bold',
    subtitle: 'text-blue-100 text-sm',
    close: 'text-white/80 hover:text-white transition-colors'
  },
  'gradient-red': {
    bar: 'bg-gradient-to-r from-red-600 to-red-700 text-white p-6',
    iconWrap: 'bg-white/20 p-2 rounded-lg',
    title: 'text-2xl font-bold',
    subtitle: 'text-red-100 text-sm',
    close: 'text-white/80 hover:text-white transition-colors'
  }
};

interface ModalHeaderProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly icon?: React.ReactNode;
  readonly onClose: () => void;
  readonly variant?: ModalHeaderVariant;
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({
  title,
  subtitle,
  icon,
  onClose,
  variant = 'emerald'
}) => {
  const v = VARIANTS[variant];
  return (
    <div className={`sticky top-0 z-10 flex items-center justify-between ${v.bar}`}>
      <div className="flex items-center gap-3">
        {icon && <div className={v.iconWrap}>{icon}</div>}
        <div>
          <h2 className={v.title}>{title}</h2>
          {subtitle && <p className={v.subtitle}>{subtitle}</p>}
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        className={v.close}
        aria-label="Cerrar"
      >
        <X className="w-6 h-6" />
      </button>
    </div>
  );
};
